import { useMemo, useState } from 'react'
import { Copy, Link2, Loader2, Search, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  useAllMaterials,
  useDeleteProfile,
  useFieldDefs,
  useProfiles,
  useSaveMaterial,
  useSaveProfile,
} from '@/hooks/useInventoryMaster'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import {
  DEFAULT_ACTIONS_PROFILE_NAME,
  actionSwitches,
  actionsProfileFor,
  actionsProfileOf,
  fieldsWithSwitch,
} from '@/lib/materialActions'
import { cn } from '@/lib/utils'
import type { ApiMaterial, ApiProfile, ProfileFieldEntry } from '@/types/inventory'
import { actionIcon } from './materialActionIcons'

/**
 * Master Data Config → Actions: sets of material row actions.
 *
 * Every tenant has a Default set with all actions on; a material uses it unless
 * it is connected to another set. A new set starts as a copy of an existing one
 * (Duplicate), so it is created already configured and only the differences
 * need switching. Not the generic profiles table the other categories use:
 * actions are defined by the code, so there is nothing to build, only switches.
 */
export function ActionsConfig() {
  const { can } = usePermission()
  const canConfigure = can('inventory.configure')
  const canConnect = can(PERMISSIONS.inventoryManage)
  const { data: profiles = [], isLoading: loadingProfiles } = useProfiles('actions')
  const { data: defs = [], isLoading: loadingDefs } = useFieldDefs('actions')
  const { data: materialList } = useAllMaterials()
  const materials = useMemo(() => materialList?.items ?? [], [materialList])
  const saveProfile = useSaveProfile()
  const deleteProfile = useDeleteProfile()

  const defaultProfile = actionsProfileOf(profiles)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // No profile yet (a tenant the backend has not provisioned): the Default set
  // is shown from the code's defaults and saved on the first change.
  const current = profiles.find((p) => p.id === selectedId) ?? defaultProfile
  const isDefault = !current || current.id === defaultProfile?.id
  const switches = actionSwitches(defs, current)
  const onCount = switches.filter((s) => s.active).length

  const [copying, setCopying] = useState(false)
  const [copyName, setCopyName] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save the change.')

  // Which set each material resolves to, counted per set for the chips.
  const usersOf = useMemo(() => {
    const by = new Map<string, ApiMaterial[]>()
    for (const m of materials) {
      const id = actionsProfileFor(profiles, m)?.id ?? ''
      by.set(id, [...(by.get(id) ?? []), m])
    }
    return by
  }, [materials, profiles])
  const currentUsers = usersOf.get(current?.id ?? '') ?? []

  function allFields(): Record<string, ProfileFieldEntry> {
    return Object.fromEntries(switches.map((s) => [s.key, { active: s.active, value: s.active }]))
  }

  /** The Default set, created from the code's defaults if the tenant has none yet. */
  async function ensureDefault(): Promise<ApiProfile> {
    if (defaultProfile) return defaultProfile
    return saveProfile.mutateAsync({
      payload: {
        profileType: 'actions',
        name: DEFAULT_ACTIONS_PROFILE_NAME,
        isDefault: true,
        fields: allFields(),
      },
    })
  }

  async function flip(key: string, on: boolean) {
    setError(null)
    try {
      const target = current ?? (await ensureDefault())
      await saveProfile.mutateAsync({
        id: target.id,
        payload: { fields: fieldsWithSwitch(switches, target, key, on) },
      })
    } catch (err) {
      fail(err)
    }
  }

  async function duplicate() {
    const name = copyName.trim()
    if (!name) return
    setError(null)
    try {
      // The Default has to exist first, or the copy would become the set every
      // unconnected material falls back to.
      await ensureDefault()
      const created = await saveProfile.mutateAsync({
        payload: { profileType: 'actions', name, isDefault: false, fields: allFields() },
      })
      setSelectedId(created.id)
      setCopying(false)
      setCopyName('')
    } catch (err) {
      fail(err)
    }
  }

  function remove() {
    if (!current || isDefault) return
    const n = currentUsers.length
    const note = n ? ` Its ${n} material${n === 1 ? '' : 's'} go back to ${defaultProfile?.name ?? 'Default'}.` : ''
    if (!window.confirm(`Delete the "${current.name}" action set?${note}`)) return
    deleteProfile.mutate(current.id, {
      onSuccess: () => setSelectedId(null),
      onError: fail,
    })
  }

  const busy = saveProfile.isPending || deleteProfile.isPending

  return (
    <div className='rounded-2xl border border-gray-200 bg-white shadow-sm'>
      <div className='space-y-3 border-b border-gray-100 px-5 py-4'>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <h3 className='text-sm font-semibold text-gray-900'>Material actions</h3>
            <p className='text-xs text-gray-500'>
              The buttons on a material row. Every material uses the default set unless it is connected to another.
            </p>
          </div>
          {canConfigure && !copying && (
            <Button size='sm' variant='outline' className='gap-1' onClick={() => setCopying(true)} disabled={busy}>
              <Copy className='h-3.5 w-3.5' />
              Duplicate set
            </Button>
          )}
        </div>

        {/* One chip per set; the selected one is what the list below edits. */}
        <div role='tablist' aria-label='Action sets' className='flex flex-wrap items-center gap-2'>
          {(profiles.length ? profiles : [null]).map((p) => {
            const id = p?.id ?? ''
            const selected = (current?.id ?? '') === id
            const users = usersOf.get(id)?.length ?? 0
            return (
              <button
                key={id || 'virtual-default'}
                type='button'
                role='tab'
                aria-selected={selected}
                onClick={() => setSelectedId(p?.id ?? null)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs',
                  selected ? 'border-primary bg-primary text-primary-foreground' : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                )}
              >
                {p?.name ?? DEFAULT_ACTIONS_PROFILE_NAME}
                {(!p || p.id === defaultProfile?.id) && <span className='opacity-70'>· default</span>}
                <span className={cn('rounded-full px-1.5 tabular-nums', selected ? 'bg-white/20' : 'bg-gray-100')}>
                  {users}
                </span>
              </button>
            )
          })}
        </div>

        {copying && (
          <form
            className='flex items-center gap-2'
            onSubmit={(e) => {
              e.preventDefault()
              void duplicate()
            }}
          >
            <span className='text-xs text-gray-500'>
              Copy of <span className='font-medium text-gray-700'>{current?.name ?? DEFAULT_ACTIONS_PROFILE_NAME}</span> named
            </span>
            <Input
              autoFocus
              value={copyName}
              onChange={(e) => setCopyName(e.target.value)}
              placeholder='e.g. Reagents'
              className='h-8 w-48 text-sm'
              aria-label='New action set name'
            />
            <Button type='submit' size='sm' disabled={!copyName.trim() || busy}>
              Create
            </Button>
            <Button type='button' size='sm' variant='ghost' onClick={() => { setCopying(false); setCopyName('') }}>
              Cancel
            </Button>
          </form>
        )}
      </div>

      <div className='flex items-center justify-between px-5 pt-3'>
        <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>
          {current?.name ?? DEFAULT_ACTIONS_PROFILE_NAME} · {onCount} of {switches.length} on
        </p>
        <div className='flex items-center gap-3'>
          {canConnect && current && (
            <Button size='sm' variant='outline' className='h-7 gap-1 text-xs' onClick={() => setConnecting(true)}>
              <Link2 className='h-3.5 w-3.5' />
              Connect materials
            </Button>
          )}
          {canConfigure && !isDefault && (
            <button
              type='button'
              onClick={remove}
              disabled={busy}
              className='flex items-center gap-1 text-xs text-gray-400 hover:text-red-600'
            >
              <Trash2 className='h-3.5 w-3.5' />
              Delete set
            </button>
          )}
        </div>
      </div>

      {loadingProfiles || loadingDefs ? (
        <p className='px-5 py-8 text-center text-sm text-muted-foreground'>Loading actions…</p>
      ) : (
        <ul className='divide-y divide-gray-100'>
          {switches.map((s) => {
            const Icon = actionIcon(s.key)
            const id = `action-switch-${s.key}`
            return (
              <li key={s.key} className='flex items-center gap-4 px-5 py-3'>
                {/* Drawn like the button on the row, so the switch is easy to match to it. */}
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border',
                    s.active ? 'border-gray-200 text-gray-700' : 'border-dashed border-gray-200 text-gray-300',
                  )}
                  aria-hidden
                >
                  <Icon className='h-4 w-4' />
                </span>
                <label htmlFor={id} className='min-w-0 flex-1'>
                  <span className={cn('block text-sm font-medium', s.active ? 'text-gray-900' : 'text-gray-400')}>
                    {s.label}
                    {s.custom && <span className='ml-2 text-xs font-normal text-gray-400'>(custom)</span>}
                  </span>
                  {s.description && <span className='block text-xs text-gray-500'>{s.description}</span>}
                </label>
                <Switch
                  id={id}
                  checked={s.active}
                  disabled={!canConfigure || busy}
                  onCheckedChange={(on) => void flip(s.key, on)}
                />
              </li>
            )
          })}
        </ul>
      )}

      <div className='border-t border-gray-100 px-5 py-3 text-xs'>
        {error ? (
          <span className='text-red-600'>{error}</span>
        ) : !canConfigure ? (
          <span className='text-gray-500'>You can see the action sets but not change them.</span>
        ) : (
          <span className='text-gray-500'>Changes save as you switch them.</span>
        )}
      </div>

      {current && (
        <ConnectMaterialsDialog
          open={connecting}
          onClose={() => setConnecting(false)}
          set={current}
          isDefault={isDefault}
          materials={materials}
          profiles={profiles}
        />
      )}
    </div>
  )
}

/**
 * Pick materials to use one action set. Ticking a material connects it, so it
 * leaves whatever set it used before; for the default set that means clearing
 * its own connection.
 */
function ConnectMaterialsDialog({
  open,
  onClose,
  set,
  isDefault,
  materials,
  profiles,
}: {
  open: boolean
  onClose: () => void
  set: ApiProfile
  isDefault: boolean
  materials: ApiMaterial[]
  profiles: ApiProfile[]
}) {
  const save = useSaveMaterial()
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  const candidates = materials.filter((m) => actionsProfileFor(profiles, m)?.id !== set.id)
  const q = query.trim().toLowerCase()
  const shown = q
    ? candidates.filter((m) => `${m.materialCode} ${m.materialName} ${m.materialType}`.toLowerCase().includes(q))
    : candidates

  function close() {
    setQuery('')
    setPicked(new Set())
    setError(null)
    onClose()
  }

  async function connect() {
    setError(null)
    try {
      for (const id of picked) {
        await save.mutateAsync({ id, payload: { actionsProfileId: isDefault ? '' : set.id } })
      }
      close()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not connect the materials.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>Connect materials to {set.name}</DialogTitle>
          <DialogDescription>
            The materials you tick show this set's buttons. Each leaves the set it used before.
          </DialogDescription>
        </DialogHeader>
        <div className='relative'>
          <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-gray-400' />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search by code, name or type'
            className='pl-8'
            aria-label='Search materials'
          />
        </div>
        <ul className='max-h-80 divide-y overflow-y-auto rounded-md border'>
          {shown.length === 0 && (
            <li className='px-3 py-6 text-center text-sm text-muted-foreground'>No materials to connect.</li>
          )}
          {shown.map((m) => {
            const id = `connect-${m.id}`
            const from = actionsProfileFor(profiles, m)?.name ?? DEFAULT_ACTIONS_PROFILE_NAME
            return (
              <li key={m.id}>
                <label htmlFor={id} className='flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50'>
                  <Checkbox
                    id={id}
                    checked={picked.has(m.id)}
                    onCheckedChange={(v) =>
                      setPicked((prev) => {
                        const next = new Set(prev)
                        if (v) next.add(m.id)
                        else next.delete(m.id)
                        return next
                      })
                    }
                  />
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-sm font-medium text-gray-900'>
                      {m.materialCode} <span className='font-normal text-gray-500'>{m.materialName}</span>
                    </span>
                    <span className='block text-xs text-gray-500'>
                      {m.materialType} · now uses {from}
                    </span>
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
        {error && <p className='text-sm text-red-600'>{error}</p>}
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void connect()} disabled={picked.size === 0 || save.isPending} className='gap-1'>
            {save.isPending && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
            Connect {picked.size || ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
